import React, { useCallback, useEffect, useState, memo } from 'react'

import Box from '@material-ui/core/Box'
import IconButton from '@material-ui/core/IconButton'
import Typography from '@material-ui/core/Typography'
import Tooltip from '@material-ui/core/Tooltip'

import makeStyles from '@material-ui/styles/makeStyles'

import SkipPreviousIcon from '@material-ui/icons/SkipPrevious'
import SkipNextIcon from '@material-ui/icons/SkipNext'

import { SpacedGroup } from '@guidde-co/shared.spaced-group'
import { SectionType } from '@guidde-co/shared.video-player.timeline-with-sections'

const useStyles = makeStyles({
    wrapper: {
        width: '100%'
    },
    chapterName: {
        fontWeight: 500,
        marginRight: '8px',
        display: '-webkit-box',
        '-webkit-line-clamp': 1,
        '-webkit-box-orient': 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        wordBreak: 'break-word'
    },
    chapterNumber: {
        fontWeight: 500,
        marginRight: '4px'
    }
})

export type CurrentChapterProps = {
    /**
     * change current time according to argument
     */
    onUpdateTime: (seconds: number) => void
    /**
     * current video time
     */
    currentTime: number
    /**
     * Array<{ start: number, end: number }>
     */
    chapters: Array<SectionType>
}

export const CurrentChapter = memo(
    ({ onUpdateTime, chapters, currentTime }: CurrentChapterProps) => {
        const classes = useStyles()

        const [activeChapter, setActiveChapter] = useState<number>(0)

        useEffect(() => {
            const index = chapters?.findIndex(
                chapter => chapter.end > currentTime
            )
            if (index === activeChapter) return
            if (index === -1) return setActiveChapter(chapters.length - 1)

            setActiveChapter(index)
        }, [chapters, currentTime, activeChapter])

        if (chapters?.length === 0) return null

        return (
            <SpacedGroup className={classes.wrapper}>
                <Box display="flex">
                    <IconButton
                        onClick={() => {
                            onUpdateTime(chapters[activeChapter - 1]?.start)
                        }}
                        size="small"
                        style={{ color: 'white' }}
                        disableRipple={true}
                        disabled={activeChapter === 0}
                    >
                        <SkipPreviousIcon />
                    </IconButton>
                    <IconButton
                        onClick={() => {
                            onUpdateTime(chapters[activeChapter + 1]?.start)
                        }}
                        size="small"
                        style={{ color: 'white' }}
                        disableRipple={true}
                        disabled={activeChapter === chapters.length - 1}
                    >
                        <SkipNextIcon />
                    </IconButton>
                </Box>

                <Tooltip title={chapters[activeChapter]?.title}>
                    <Typography variant="body2" className={classes.chapterName}>
                        {activeChapter + 1}/{chapters.length}&nbsp;
                        {chapters[activeChapter]?.title}
                    </Typography>
                </Tooltip>
            </SpacedGroup>
        )
    }
)
